import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, fmtErr } from "../lib/api";
import { roleLabel } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Workspaces() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [workspaces, setWorkspaces] = useState([]);
  const [modules, setModules] = useState([]);
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [addUserId, setAddUserId] = useState("");

  const load = async () => {
    try {
      const reqs = [api.get("/workspaces"), api.get("/workspaces/meta")];
      if (isAdmin) reqs.push(api.get("/users"));
      const [w, m, u] = await Promise.all(reqs);
      setWorkspaces(w.data);
      setModules(m.data.modules || []);
      if (u) setUsers(u.data);
      setSel((cur) => (cur && w.data.some((x) => x.id === cur) ? cur : w.data[0]?.id || null));
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { if (user) load(); }, [user]); // eslint-disable-line

  const ws = workspaces.find((w) => w.id === sel) || null;
  const modLabel = useMemo(() => Object.fromEntries(modules.map((m) => [m.key, m.label])), [modules]);

  const createWs = async (e) => {
    e.preventDefault();
    try {
      await api.post("/workspaces", form);
      toast.success("Workspace created");
      setShowNew(false); setForm({ name: "", description: "" }); load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const deleteWs = async (w) => {
    try {
      await api.delete(`/workspaces/${w.id}`);
      toast.success("Workspace deleted"); load();
    } catch (err) {
      const msg = fmtErr(err);
      if (String(err?.response?.status) === "409") {
        if (window.confirm(`${msg}\n\nDetach those projects (they become org-wide) and delete?`)) {
          try { await api.delete(`/workspaces/${w.id}?force=true`); toast.success("Workspace deleted"); load(); }
          catch (e2) { toast.error(fmtErr(e2)); }
        }
      } else toast.error(msg);
    }
  };

  const addMember = async () => {
    if (!addUserId) return;
    try {
      await api.post(`/workspaces/${sel}/members`, { user_id: addUserId, modules: modules.map((m) => m.key) });
      toast.success("Member added with all module access");
      setAddUserId(""); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const toggleModule = async (m, key) => {
    const mods = m.modules.includes(key) ? m.modules.filter((x) => x !== key) : [...m.modules, key];
    try {
      await api.patch(`/workspaces/${sel}/members/${m.user_id}`, { modules: mods });
      setWorkspaces((all) => all.map((w) => w.id !== sel ? w : {
        ...w, members: w.members.map((mm) => mm.user_id === m.user_id ? { ...mm, modules: mods } : mm),
      }));
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const removeMember = async (m) => {
    try { await api.delete(`/workspaces/${sel}/members/${m.user_id}`); toast.success("Member removed"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const nonMembers = useMemo(() => {
    if (!ws?.members) return [];
    const ids = new Set(ws.members.map((m) => m.user_id));
    return users.filter((u) => !ids.has(u.id) && u.role !== "admin");
  }, [users, ws]);

  if (!isAdmin) {
    return (
      <div data-testid="workspaces-page" className="space-y-3">
        <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">
          My Workspaces · {workspaces.length}
        </div>
        {workspaces.length === 0 && (
          <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-8 text-center text-xs text-[var(--c-muted)]" data-testid="workspaces-empty">
            You are not assigned to any workspace yet. Contact your admin for access.
          </div>
        )}
        {workspaces.map((w) => (
          <div key={w.id} data-testid={`workspace-item-${w.id}`}
            className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4">
            <div className="text-sm font-medium text-[var(--c-text)]">{w.name}</div>
            {w.description && <div className="text-xs text-[var(--c-muted)] mt-0.5">{w.description}</div>}
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {(w.modules || []).map((m) => (
                <span key={m} className="text-[10px] font-mono px-1.5 py-0.5 bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm text-[var(--c-accent)]">
                  {modLabel[m] || m}
                </span>
              ))}
              {(w.modules || []).length === 0 && <span className="text-[10px] text-[var(--c-muted)]">No module access granted</span>}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex gap-5 h-full items-start" data-testid="workspaces-page">
      <div className="w-64 shrink-0 space-y-2">
        <Button data-testid="add-workspace-btn" onClick={() => setShowNew(true)} size="sm"
          className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
          <Plus size={13} className="mr-1" /> New Workspace
        </Button>
        {workspaces.length === 0 && (
          <div className="bg-[var(--c-surface)] border border-dashed border-[var(--c-border)] rounded-sm p-4 text-center text-xs text-[var(--c-muted)]" data-testid="workspaces-empty">
            No workspaces yet. Create one to isolate projects and teams.
          </div>
        )}
        {workspaces.map((w) => (
          <button key={w.id} data-testid={`workspace-item-${w.id}`} onClick={() => setSel(w.id)}
            className={`w-full text-left px-3 py-2.5 rounded-sm border transition-colors duration-150 ${
              sel === w.id ? "bg-[var(--c-accent)]/10 border-[var(--c-accent)]/50" : "bg-[var(--c-surface)] border-[var(--c-border)] hover:border-[var(--c-muted)]"}`}>
            <div className="flex items-center gap-2">
              <Buildings size={14} className="text-[var(--c-accent)] shrink-0" />
              <span className="text-xs font-medium text-[var(--c-text)] truncate">{w.name}</span>
            </div>
            <div className="text-[10px] font-mono text-[var(--c-muted)] mt-1">
              {(w.members || []).length} members · {w.project_count || 0} projects
            </div>
          </button>
        ))}
      </div>

      <div className="flex-1 bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5 min-w-0" data-testid="workspace-detail">
        {!ws ? (
          <div className="text-center text-xs text-[var(--c-muted)] py-10">Select a workspace to manage its members and module access.</div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3 mb-1">
              <div>
                <div className="text-sm font-medium text-[var(--c-text)]" data-testid="ws-detail-name">{ws.name}</div>
                {ws.description && <div className="text-xs text-[var(--c-muted)] mt-0.5">{ws.description}</div>}
              </div>
              <Button variant="outline" size="sm" data-testid={`ws-delete-${ws.id}`} onClick={() => deleteWs(ws)}
                className="border-[var(--c-red)]/50 text-[var(--c-red)] hover:bg-[var(--c-red)]/10 rounded-sm h-7 text-xs shrink-0">
                <Trash size={12} className="mr-1" /> Delete
              </Button>
            </div>
            <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mt-4 mb-2">
              Members & module access · projects in this workspace are visible only to allocated members
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="members-table">
                <thead>
                  <tr className="border-b border-[var(--c-border)]">
                    <th className="text-left px-2 py-2 text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">Member</th>
                    {modules.map((m) => (
                      <th key={m.key} title={m.label}
                        className="px-1 py-2 text-[9px] font-mono tracking-wider uppercase text-[var(--c-muted)] text-center">{m.label.split(" ")[0]}</th>
                    ))}
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {(ws.members || []).map((m) => (
                    <tr key={m.user_id} className="border-b border-[var(--c-border)]/60 last:border-0" data-testid={`member-row-${m.email}`}>
                      <td className="px-2 py-2">
                        <div className="text-xs font-medium text-[var(--c-text)]">{m.name}</div>
                        <div className="text-[10px] text-[var(--c-muted)]">{m.email} · {roleLabel(m.role)}</div>
                      </td>
                      {modules.map((mod) => (
                        <td key={mod.key} className="text-center">
                          <input type="checkbox" data-testid={`mod-check-${m.user_id}-${mod.key}`}
                            checked={m.modules.includes(mod.key)} onChange={() => toggleModule(m, mod.key)}
                            className="accent-[var(--c-accent)] w-3.5 h-3.5 cursor-pointer" />
                        </td>
                      ))}
                      <td className="text-center">
                        <button data-testid={`remove-member-${m.user_id}`} onClick={() => removeMember(m)}
                          className="text-[var(--c-muted)] hover:text-[var(--c-red)] transition-colors duration-150">
                          <Trash size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {(ws.members || []).length === 0 && (
                    <tr><td colSpan={modules.length + 2} className="px-2 py-6 text-center text-xs text-[var(--c-muted)]">
                      No members yet — add team members below.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[var(--c-border)]">
              <Select value={addUserId} onValueChange={setAddUserId}>
                <SelectTrigger data-testid="add-member-select" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs w-72">
                  <SelectValue placeholder="Select a team member to add" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                  {nonMembers.map((u) => (
                    <SelectItem key={u.id} value={u.id} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">
                      {u.name} · {u.email}
                    </SelectItem>
                  ))}
                  {nonMembers.length === 0 && <div className="text-xs text-[var(--c-muted)] px-3 py-2">Everyone is already a member</div>}
                </SelectContent>
              </Select>
              <Button data-testid="add-member-btn" onClick={addMember} disabled={!addUserId} size="sm"
                className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
                <Plus size={13} className="mr-1" /> Add Member
              </Button>
              <span className="text-[10px] text-[var(--c-muted)]">New members start with all modules — uncheck to restrict.</span>
            </div>
          </>
        )}
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)]" data-testid="new-workspace-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--c-text)]">New Workspace</DialogTitle>
            <DialogDescription className="sr-only">Create an isolated workspace</DialogDescription>
          </DialogHeader>
          <form onSubmit={createWs} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[var(--c-muted)] text-xs">Name</Label>
              <Input data-testid="ws-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Transformer Division" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[var(--c-muted)] text-xs">Description</Label>
              <Input data-testid="ws-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Optional" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" />
            </div>
            <div className="text-[10px] text-[var(--c-muted)] leading-relaxed">
              Projects assigned to a workspace are visible only to its allocated members. Module access (Design, Validation, Drawings…) is granted per member after creation.
            </div>
            <Button data-testid="ws-submit" className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">
              Create Workspace
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
